import { supabase } from '@/integrations/supabase/client'
import type { MonthlyData, CategoryData } from '@/lib/types'
import { getCategoryColor } from '@/lib/utils/category-helpers'

export interface StatisticsTotals {
  totalBalance: number
  totalIncome: number
  totalExpenses: number
  monthOverMonthChange: number
  hasPastData: boolean
}

/**
 * Get monthly income and expenses data for the last N months
 */
export async function fetchMonthlyData(userId: string, months: number = 5, subAccountId?: string): Promise<MonthlyData[]> {
  // Calculate date range
  const endDate = new Date()
  const startDate = new Date()
  startDate.setMonth(startDate.getMonth() - months + 1)
  startDate.setDate(1) // First day of the month

  // Build queries with conditional sub-account filtering
  let expensesQuery: any = supabase
    .from('user_expenses')
    .select('amount, created_at')
    .eq('user_id', userId)
    .gte('created_at', startDate.toISOString())
    .lte('created_at', endDate.toISOString())

  let incomesQuery: any = supabase
    .from('user_incomes')
    .select('amount, created_at')
    .eq('user_id', userId)
    .gte('created_at', startDate.toISOString())
    .lte('created_at', endDate.toISOString())

  // Apply sub-account filter if specific account is selected
  if (subAccountId && subAccountId !== 'all') {
    expensesQuery = expensesQuery.eq('sub_account_id', subAccountId)
    incomesQuery = incomesQuery.eq('sub_account_id', subAccountId)
  }

  // Fetch expenses and incomes for the date range
  const [expensesResult, incomesResult] = await Promise.all([
    expensesQuery,
    incomesQuery
  ])

  if (expensesResult.error) {
    console.error('Error fetching expenses for statistics:', expensesResult.error)
    throw new Error('Failed to fetch expense statistics')
  }

  if (incomesResult.error) {
    console.error('Error fetching incomes for statistics:', incomesResult.error)
    throw new Error('Failed to fetch income statistics')
  }

  // Group data by month
  const monthlyMap = new Map<string, { income: number; expenses: number; date: Date }>()

  // Process expenses
  expensesResult.data.forEach((expense) => {
    const date = new Date(expense.created_at)
    const monthKey = `${date.getFullYear()}-${String(date.getMonth()).padStart(2, '0')}`

    if (!monthlyMap.has(monthKey)) {
      monthlyMap.set(monthKey, { income: 0, expenses: 0, date })
    }

    const current = monthlyMap.get(monthKey)!
    current.expenses += Number(expense.amount)
  })

  // Process incomes
  incomesResult.data.forEach((income) => {
    const date = new Date(income.created_at)
    const monthKey = `${date.getFullYear()}-${String(date.getMonth()).padStart(2, '0')}`

    if (!monthlyMap.has(monthKey)) {
      monthlyMap.set(monthKey, { income: 0, expenses: 0, date })
    }

    const current = monthlyMap.get(monthKey)!
    current.income += Number(income.amount)
  })

  // Convert to array and sort by date
  const monthlyDataArray: MonthlyData[] = Array.from(monthlyMap.entries())
    .map(([_, data]) => ({
      month: data.date.toLocaleDateString('en-US', { month: 'short' }),
      income: Math.round(data.income * 100) / 100,
      expenses: Math.round(data.expenses * 100) / 100
    }))
    .sort((a, b) => {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return months.indexOf(a.month) - months.indexOf(b.month)
    })

  // Fill in missing months with zero data
  const result: MonthlyData[] = []
  const currentDate = new Date(startDate)

  for (let i = 0; i < months; i++) {
    const monthName = currentDate.toLocaleDateString('en-US', { month: 'short' })
    const existingData = monthlyDataArray.find(d => d.month === monthName)

    result.push(existingData || {
      month: monthName,
      income: 0,
      expenses: 0
    })

    currentDate.setMonth(currentDate.getMonth() + 1)
  }

  return result
}

/**
 * Get top spending categories for current month
 */
export async function fetchCategoryData(userId: string, limit: number = 5, subAccountId?: string): Promise<CategoryData[]> {
  // Get expenses for current month
  const startOfMonth = new Date()
  startOfMonth.setDate(1)
  startOfMonth.setHours(0, 0, 0, 0)

  let query: any = supabase
    .from('user_expenses')
    .select('category, amount')
    .eq('user_id', userId)
    .gte('created_at', startOfMonth.toISOString())

  // Apply sub-account filter if specific account is selected
  if (subAccountId && subAccountId !== 'all') {
    query = query.eq('sub_account_id', subAccountId)
  }

  const { data, error } = await query

  if (error) {
    console.error('Error fetching category data:', error)
    throw new Error('Failed to fetch category statistics')
  }

  // Group by category and sum amounts
  const categoryMap = new Map<string, number>()

  data.forEach((expense) => {
    const current = categoryMap.get(expense.category) || 0
    categoryMap.set(expense.category, current + Number(expense.amount))
  })

  // Convert to array, sort by amount, and take top N
  const categoryDataArray: CategoryData[] = Array.from(categoryMap.entries())
    .map(([category, amount]) => ({
      category,
      amount: Math.round(amount * 100) / 100,
      color: getCategoryColor(category)
    }))
    .sort((a, b) => b.amount - a.amount)
    .slice(0, limit)

  return categoryDataArray
}

/**
 * Calculate statistics totals and month-over-month change
 */
export async function fetchStatisticsTotals(userId: string, subAccountId?: string): Promise<StatisticsTotals> {
  // Get current month's date range
  const now = new Date()
  const startOfCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1)
  const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
  const endOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59)

  // Build queries for all-time totals
  let allExpensesQuery: any = supabase
    .from('user_expenses')
    .select('amount')
    .eq('user_id', userId)

  let allIncomesQuery: any = supabase
    .from('user_incomes')
    .select('amount')
    .eq('user_id', userId)

  // Apply sub-account filter if specific account is selected
  if (subAccountId && subAccountId !== 'all') {
    allExpensesQuery = allExpensesQuery.eq('sub_account_id', subAccountId)
    allIncomesQuery = allIncomesQuery.eq('sub_account_id', subAccountId)
  }

  // Fetch all-time totals
  const [allExpensesResult, allIncomesResult] = await Promise.all([
    allExpensesQuery,
    allIncomesQuery
  ])

  if (allExpensesResult.error || allIncomesResult.error) {
    console.error('Error fetching totals:', allExpensesResult.error || allIncomesResult.error)
    throw new Error('Failed to calculate totals')
  }

  // Calculate all-time totals
  const totalExpenses = allExpensesResult.data.reduce((sum, e) => sum + Number(e.amount), 0)
  const totalIncome = allIncomesResult.data.reduce((sum, i) => sum + Number(i.amount), 0)
  const totalBalance = totalIncome - totalExpenses

  // Build queries for current month data
  let currentExpensesQuery: any = supabase
    .from('user_expenses')
    .select('amount')
    .eq('user_id', userId)
    .gte('created_at', startOfCurrentMonth.toISOString())

  let currentIncomesQuery: any = supabase
    .from('user_incomes')
    .select('amount')
    .eq('user_id', userId)
    .gte('created_at', startOfCurrentMonth.toISOString())

  // Build queries for last month data
  let lastExpensesQuery: any = supabase
    .from('user_expenses')
    .select('amount')
    .eq('user_id', userId)
    .gte('created_at', startOfLastMonth.toISOString())
    .lt('created_at', startOfCurrentMonth.toISOString())

  let lastIncomesQuery: any = supabase
    .from('user_incomes')
    .select('amount')
    .eq('user_id', userId)
    .gte('created_at', startOfLastMonth.toISOString())
    .lt('created_at', startOfCurrentMonth.toISOString())

  // Apply sub-account filter if specific account is selected
  if (subAccountId && subAccountId !== 'all') {
    currentExpensesQuery = currentExpensesQuery.eq('sub_account_id', subAccountId)
    currentIncomesQuery = currentIncomesQuery.eq('sub_account_id', subAccountId)
    lastExpensesQuery = lastExpensesQuery.eq('sub_account_id', subAccountId)
    lastIncomesQuery = lastIncomesQuery.eq('sub_account_id', subAccountId)
  }

  // Fetch current month data
  const [currentExpensesResult, currentIncomesResult] = await Promise.all([
    currentExpensesQuery,
    currentIncomesQuery
  ])

  // Fetch last month data
  const [lastExpensesResult, lastIncomesResult] = await Promise.all([
    lastExpensesQuery,
    lastIncomesQuery
  ])

  // Calculate monthly balances
  const currentMonthExpenses = currentExpensesResult.data?.reduce((sum, e) => sum + Number(e.amount), 0) || 0
  const currentMonthIncome = currentIncomesResult.data?.reduce((sum, i) => sum + Number(i.amount), 0) || 0
  const currentMonthBalance = currentMonthIncome - currentMonthExpenses

  const lastMonthExpenses = lastExpensesResult.data?.reduce((sum, e) => sum + Number(e.amount), 0) || 0
  const lastMonthIncome = lastIncomesResult.data?.reduce((sum, i) => sum + Number(i.amount), 0) || 0
  const lastMonthBalance = lastMonthIncome - lastMonthExpenses

  // Check if there's any data in the previous period
  const hasPastData = (lastExpensesResult.data?.length ?? 0) > 0 || (lastIncomesResult.data?.length ?? 0) > 0

  // Calculate month-over-month percentage change
  let monthOverMonthChange = 0
  if (lastMonthBalance !== 0) {
    monthOverMonthChange = ((currentMonthBalance - lastMonthBalance) / Math.abs(lastMonthBalance)) * 100
  } else if (currentMonthBalance > 0) {
    monthOverMonthChange = 100
  }

  return {
    totalBalance: Math.round(totalBalance * 100) / 100,
    totalIncome: Math.round(totalIncome * 100) / 100,
    totalExpenses: Math.round(totalExpenses * 100) / 100,
    monthOverMonthChange: Math.round(monthOverMonthChange * 10) / 10,
    hasPastData
  }
}

/**
 * Calculate week-over-week change
 */
export async function fetchWeeklyComparison(userId: string, subAccountId?: string): Promise<StatisticsTotals> {
  // Get date ranges for current and previous week
  const now = new Date()
  const currentWeekStart = new Date(now)
  currentWeekStart.setDate(now.getDate() - now.getDay()) // Start of current week (Sunday)
  currentWeekStart.setHours(0, 0, 0, 0)

  const previousWeekStart = new Date(currentWeekStart)
  previousWeekStart.setDate(currentWeekStart.getDate() - 7)

  const previousWeekEnd = new Date(currentWeekStart)
  previousWeekEnd.setMilliseconds(-1)

  // Build queries for all-time totals
  let allExpensesQuery = (supabase
    .from('user_expenses') as any)
    .select('amount')
    .eq('user_id', userId)

  let allIncomesQuery = (supabase
    .from('user_incomes') as any)
    .select('amount')
    .eq('user_id', userId)

  // Apply sub-account filter if specific account is selected
  if (subAccountId && subAccountId !== 'all') {
    allExpensesQuery = allExpensesQuery.eq('sub_account_id', subAccountId)
    allIncomesQuery = allIncomesQuery.eq('sub_account_id', subAccountId)
  }

  // Fetch all-time totals
  const [allExpensesResult, allIncomesResult] = await Promise.all([
    allExpensesQuery,
    allIncomesQuery
  ])

  if (allExpensesResult.error || allIncomesResult.error) {
    throw new Error('Failed to calculate totals')
  }

  const totalExpenses = allExpensesResult.data.reduce((sum, e) => sum + Number(e.amount), 0)
  const totalIncome = allIncomesResult.data.reduce((sum, i) => sum + Number(i.amount), 0)
  const totalBalance = totalIncome - totalExpenses

  // Build queries for current week data
  let currentWeekExpensesQuery = (supabase
    .from('user_expenses') as any)
    .select('amount')
    .eq('user_id', userId)
    .gte('created_at', currentWeekStart.toISOString())

  let currentWeekIncomesQuery = (supabase
    .from('user_incomes') as any)
    .select('amount')
    .eq('user_id', userId)
    .gte('created_at', currentWeekStart.toISOString())

  // Build queries for previous week data
  let previousWeekExpensesQuery = (supabase
    .from('user_expenses') as any)
    .select('amount')
    .eq('user_id', userId)
    .gte('created_at', previousWeekStart.toISOString())
    .lte('created_at', previousWeekEnd.toISOString())

  let previousWeekIncomesQuery = (supabase
    .from('user_incomes') as any)
    .select('amount')
    .eq('user_id', userId)
    .gte('created_at', previousWeekStart.toISOString())
    .lte('created_at', previousWeekEnd.toISOString())

  // Apply sub-account filter if specific account is selected
  if (subAccountId && subAccountId !== 'all') {
    currentWeekExpensesQuery = currentWeekExpensesQuery.eq('sub_account_id', subAccountId)
    currentWeekIncomesQuery = currentWeekIncomesQuery.eq('sub_account_id', subAccountId)
    previousWeekExpensesQuery = previousWeekExpensesQuery.eq('sub_account_id', subAccountId)
    previousWeekIncomesQuery = previousWeekIncomesQuery.eq('sub_account_id', subAccountId)
  }

  // Fetch current week data
  const [currentWeekExpensesResult, currentWeekIncomesResult] = await Promise.all([
    currentWeekExpensesQuery,
    currentWeekIncomesQuery
  ])

  // Fetch previous week data
  const [previousWeekExpensesResult, previousWeekIncomesResult] = await Promise.all([
    previousWeekExpensesQuery,
    previousWeekIncomesQuery
  ])

  // Calculate weekly balances
  const currentWeekExpenses = currentWeekExpensesResult.data?.reduce((sum, e) => sum + Number(e.amount), 0) || 0
  const currentWeekIncome = currentWeekIncomesResult.data?.reduce((sum, i) => sum + Number(i.amount), 0) || 0
  const currentWeekBalance = currentWeekIncome - currentWeekExpenses

  const previousWeekExpenses = previousWeekExpensesResult.data?.reduce((sum, e) => sum + Number(e.amount), 0) || 0
  const previousWeekIncome = previousWeekIncomesResult.data?.reduce((sum, i) => sum + Number(i.amount), 0) || 0
  const previousWeekBalance = previousWeekIncome - previousWeekExpenses

  // Check if there's any data in the previous period
  const hasPastData = (previousWeekExpensesResult.data?.length ?? 0) > 0 || (previousWeekIncomesResult.data?.length ?? 0) > 0

  // Calculate week-over-week percentage change
  let weekOverWeekChange = 0
  if (previousWeekBalance !== 0) {
    weekOverWeekChange = ((currentWeekBalance - previousWeekBalance) / Math.abs(previousWeekBalance)) * 100
  } else if (currentWeekBalance > 0) {
    weekOverWeekChange = 100
  } else if (currentWeekBalance < 0) {
    weekOverWeekChange = -100
  }

  return {
    totalBalance: Math.round(totalBalance * 100) / 100,
    totalIncome: Math.round(totalIncome * 100) / 100,
    totalExpenses: Math.round(totalExpenses * 100) / 100,
    monthOverMonthChange: Math.round(weekOverWeekChange * 10) / 10,
    hasPastData
  }
}

/**
 * Calculate year-over-year change
 */
export async function fetchYearlyComparison(userId: string, subAccountId?: string): Promise<StatisticsTotals> {
  // Get date ranges for current and previous year
  const now = new Date()
  const currentYearStart = new Date(now.getFullYear(), 0, 1)
  const previousYearStart = new Date(now.getFullYear() - 1, 0, 1)
  const previousYearEnd = new Date(now.getFullYear(), 0, 0, 23, 59, 59)

  // Build queries for all-time totals
  let allExpensesQuery = (supabase
    .from('user_expenses') as any)
    .select('amount')
    .eq('user_id', userId)

  let allIncomesQuery = (supabase
    .from('user_incomes') as any)
    .select('amount')
    .eq('user_id', userId)

  // Apply sub-account filter if specific account is selected
  if (subAccountId && subAccountId !== 'all') {
    allExpensesQuery = allExpensesQuery.eq('sub_account_id', subAccountId)
    allIncomesQuery = allIncomesQuery.eq('sub_account_id', subAccountId)
  }

  // Fetch all-time totals
  const [allExpensesResult, allIncomesResult] = await Promise.all([
    allExpensesQuery,
    allIncomesQuery
  ])

  if (allExpensesResult.error || allIncomesResult.error) {
    throw new Error('Failed to calculate totals')
  }

  const totalExpenses = allExpensesResult.data.reduce((sum, e) => sum + Number(e.amount), 0)
  const totalIncome = allIncomesResult.data.reduce((sum, i) => sum + Number(i.amount), 0)
  const totalBalance = totalIncome - totalExpenses

  // Build queries for current year data
  let currentYearExpensesQuery = (supabase
    .from('user_expenses') as any)
    .select('amount')
    .eq('user_id', userId)
    .gte('created_at', currentYearStart.toISOString())

  let currentYearIncomesQuery = (supabase
    .from('user_incomes') as any)
    .select('amount')
    .eq('user_id', userId)
    .gte('created_at', currentYearStart.toISOString())

  // Build queries for previous year data
  let previousYearExpensesQuery = (supabase
    .from('user_expenses') as any)
    .select('amount')
    .eq('user_id', userId)
    .gte('created_at', previousYearStart.toISOString())
    .lte('created_at', previousYearEnd.toISOString())

  let previousYearIncomesQuery = (supabase
    .from('user_incomes') as any)
    .select('amount')
    .eq('user_id', userId)
    .gte('created_at', previousYearStart.toISOString())
    .lte('created_at', previousYearEnd.toISOString())

  // Apply sub-account filter if specific account is selected
  if (subAccountId && subAccountId !== 'all') {
    currentYearExpensesQuery = currentYearExpensesQuery.eq('sub_account_id', subAccountId)
    currentYearIncomesQuery = currentYearIncomesQuery.eq('sub_account_id', subAccountId)
    previousYearExpensesQuery = previousYearExpensesQuery.eq('sub_account_id', subAccountId)
    previousYearIncomesQuery = previousYearIncomesQuery.eq('sub_account_id', subAccountId)
  }

  // Fetch current year data
  const [currentYearExpensesResult, currentYearIncomesResult] = await Promise.all([
    currentYearExpensesQuery,
    currentYearIncomesQuery
  ])

  // Fetch previous year data
  const [previousYearExpensesResult, previousYearIncomesResult] = await Promise.all([
    previousYearExpensesQuery,
    previousYearIncomesQuery
  ])

  // Calculate yearly balances
  const currentYearExpenses = currentYearExpensesResult.data?.reduce((sum, e) => sum + Number(e.amount), 0) || 0
  const currentYearIncome = currentYearIncomesResult.data?.reduce((sum, i) => sum + Number(i.amount), 0) || 0
  const currentYearBalance = currentYearIncome - currentYearExpenses

  const previousYearExpenses = previousYearExpensesResult.data?.reduce((sum, e) => sum + Number(e.amount), 0) || 0
  const previousYearIncome = previousYearIncomesResult.data?.reduce((sum, i) => sum + Number(i.amount), 0) || 0
  const previousYearBalance = previousYearIncome - previousYearExpenses

  // Check if there's any data in the previous period
  const hasPastData = (previousYearExpensesResult.data?.length ?? 0) > 0 || (previousYearIncomesResult.data?.length ?? 0) > 0

  // Calculate year-over-year percentage change
  let yearOverYearChange = 0
  if (previousYearBalance !== 0) {
    yearOverYearChange = ((currentYearBalance - previousYearBalance) / Math.abs(previousYearBalance)) * 100
  } else if (currentYearBalance > 0) {
    yearOverYearChange = 100
  } else if (currentYearBalance < 0) {
    yearOverYearChange = -100
  }

  return {
    totalBalance: Math.round(totalBalance * 100) / 100,
    totalIncome: Math.round(totalIncome * 100) / 100,
    totalExpenses: Math.round(totalExpenses * 100) / 100,
    monthOverMonthChange: Math.round(yearOverYearChange * 10) / 10,
    hasPastData
  }
}

/**
 * Fetch period-based comparison based on selected time period
 */
export async function fetchPeriodComparison(
  userId: string,
  period: 'week' | 'month' | 'year',
  subAccountId?: string
): Promise<StatisticsTotals> {
  switch (period) {
    case 'week':
      return fetchWeeklyComparison(userId, subAccountId)
    case 'year':
      return fetchYearlyComparison(userId, subAccountId)
    case 'month':
    default:
      return fetchStatisticsTotals(userId, subAccountId)
  }
}

/**
 * Fetch weekly data for statistics
 */
export async function fetchWeeklyData(userId: string, weeks: number = 4, subAccountId?: string): Promise<MonthlyData[]> {
  const endDate = new Date()
  const startDate = new Date()
  startDate.setDate(startDate.getDate() - (weeks * 7))

  // Build queries with conditional sub-account filtering
  let expensesQuery = supabase
    .from('user_expenses')
    .select('amount, created_at')
    .eq('user_id', userId)
    .gte('created_at', startDate.toISOString())
    .lte('created_at', endDate.toISOString())

  let incomesQuery = supabase
    .from('user_incomes')
    .select('amount, created_at')
    .eq('user_id', userId)
    .gte('created_at', startDate.toISOString())
    .lte('created_at', endDate.toISOString())

  // Apply sub-account filter if specific account is selected
  if (subAccountId && subAccountId !== 'all') {
    expensesQuery = (expensesQuery as any).eq('sub_account_id', subAccountId)
    incomesQuery = (incomesQuery as any).eq('sub_account_id', subAccountId)
  }

  const [expensesResult, incomesResult] = await Promise.all([
    expensesQuery,
    incomesQuery
  ])

  if (expensesResult.error || incomesResult.error) {
    throw new Error('Failed to fetch weekly data')
  }

  // Group by week
  const weeklyMap = new Map<string, { income: number; expenses: number; weekStart: Date }>()

  const getWeekKey = (date: Date) => {
    const weekStart = new Date(date)
    weekStart.setDate(date.getDate() - date.getDay()) // Start of week (Sunday)
    return weekStart.toISOString().split('T')[0]
  }

  expensesResult.data.forEach((expense) => {
    const date = new Date(expense.created_at)
    const weekKey = getWeekKey(date)

    if (!weeklyMap.has(weekKey)) {
      weeklyMap.set(weekKey, {
        income: 0,
        expenses: 0,
        weekStart: new Date(weekKey)
      })
    }

    weeklyMap.get(weekKey)!.expenses += Number(expense.amount)
  })

  incomesResult.data.forEach((income) => {
    const date = new Date(income.created_at)
    const weekKey = getWeekKey(date)

    if (!weeklyMap.has(weekKey)) {
      weeklyMap.set(weekKey, {
        income: 0,
        expenses: 0,
        weekStart: new Date(weekKey)
      })
    }

    weeklyMap.get(weekKey)!.income += Number(income.amount)
  })

  return Array.from(weeklyMap.values())
    .sort((a, b) => a.weekStart.getTime() - b.weekStart.getTime())
    .map((data, index) => ({
      month: `Week ${index + 1}`,
      income: Math.round(data.income * 100) / 100,
      expenses: Math.round(data.expenses * 100) / 100
    }))
}

/**
 * Fetch yearly data for statistics
 */
export async function fetchYearlyData(userId: string, years: number = 2, subAccountId?: string): Promise<MonthlyData[]> {
  const endDate = new Date()
  const startDate = new Date()
  startDate.setFullYear(startDate.getFullYear() - years)

  // Build queries with conditional sub-account filtering
  let expensesQuery = supabase
    .from('user_expenses')
    .select('amount, created_at')
    .eq('user_id', userId)
    .gte('created_at', startDate.toISOString())
    .lte('created_at', endDate.toISOString())

  let incomesQuery = supabase
    .from('user_incomes')
    .select('amount, created_at')
    .eq('user_id', userId)
    .gte('created_at', startDate.toISOString())
    .lte('created_at', endDate.toISOString())

  // Apply sub-account filter if specific account is selected
  if (subAccountId && subAccountId !== 'all') {
    expensesQuery = (expensesQuery as any).eq('sub_account_id', subAccountId)
    incomesQuery = (incomesQuery as any).eq('sub_account_id', subAccountId)
  }

  const [expensesResult, incomesResult] = await Promise.all([
    expensesQuery,
    incomesQuery
  ])

  if (expensesResult.error || incomesResult.error) {
    throw new Error('Failed to fetch yearly data')
  }

  // Group by year
  const yearlyMap = new Map<number, { income: number; expenses: number }>()

  expensesResult.data.forEach((expense) => {
    const year = new Date(expense.created_at).getFullYear()

    if (!yearlyMap.has(year)) {
      yearlyMap.set(year, { income: 0, expenses: 0 })
    }

    yearlyMap.get(year)!.expenses += Number(expense.amount)
  })

  incomesResult.data.forEach((income) => {
    const year = new Date(income.created_at).getFullYear()

    if (!yearlyMap.has(year)) {
      yearlyMap.set(year, { income: 0, expenses: 0 })
    }

    yearlyMap.get(year)!.income += Number(income.amount)
  })

  return Array.from(yearlyMap.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([year, data]) => ({
      month: year.toString(),
      income: Math.round(data.income * 100) / 100,
      expenses: Math.round(data.expenses * 100) / 100
    }))
}
